import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Menu,
  MenuItem,
  IconButton,
  Modal,
  Typography,
  Select,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  TextField,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Alert
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import axios from "axios";
import api,  { API_BASE_URL } from "../../../utils/api";

const EditWorkOrder = ({ workOrderDetails, onUpdate, showAsButton = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [updatedStatus, setUpdatedStatus] = useState(workOrderDetails.status || "");
  const [isSaving, setIsSaving] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [isLoadingStatuses, setIsLoadingStatuses] = useState(true);
  const [commentText, setCommentText] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const fileInputRef = useRef(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const currentUserId = parseInt(JSON.parse(localStorage.getItem("user") || "{}")?.userId, 10);





  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const res = await api.get(`/statuses/workorders`);
        setStatuses(res.data);
      } catch (error) {
        console.error("Failed to fetch statuses:", error);
        setStatuses([]);
      } finally {
        setIsLoadingStatuses(false);
      }
    };
    fetchStatuses();
  }, []);

  const now = new Date();
  const localResolvedAt = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")} ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`;

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleEditModalOpen = () => {
    setIsEditModalOpen(true);
    handleMenuClose();
  };
  const handleEditModalClose = () => setIsEditModalOpen(false);

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleSubmitAll = async () => {
    const isResolved = updatedStatus.toLowerCase().includes("resolved");
  
    try {
      setIsSaving(true);
  
      const formData = new FormData();
      formData.append("status", updatedStatus);
      formData.append("property_id", workOrderDetails.property_id);
      formData.append("assign_to", workOrderDetails.assign_to || "");
      if (isResolved) {
        formData.append("resolved_by", currentUserId);
      }
  
      if (commentText.trim()) {
        formData.append("comment_text", commentText.trim());
        formData.append("is_internal", isInternal ? 1 : 0);
        formData.append("user_id", currentUserId);
       


        if (workOrderDetails.event_id && !isNaN(workOrderDetails.event_id)) {
          formData.append("event_id", workOrderDetails.event_id);
        }
        const file = fileInputRef.current?.files[0];
        if (file) {
          formData.append("upload_urls", file);
        }
      }
  
      // ✅ THIS is your only request now:
      await api.put(`/editWorkOrder/${workOrderDetails.order_id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
  
      setSnackbarMessage("✅ Work order updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      onUpdate();
  
      handleEditModalClose();
      setCommentText("");
      setIsInternal(false);
      if (fileInputRef.current) fileInputRef.current.value = null;
  
    } catch (error) {
      console.error("❌ Error during submission:", error);
      setSnackbarMessage("❌ Failed to submit data. Check console.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsSaving(false);
    }
  };
  

  return (
    <Box>
      {showAsButton ? (
        <Button
          variant="contained"
          onClick={handleEditModalOpen}
          startIcon={<AutorenewIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            fontSize: "14px",
            borderRadius: "20px",
            px: 3,
            py: 1,
            backgroundColor: "#1976d2",
            color: "#ffffff",
            boxShadow: "0px 2px 6px rgba(25, 118, 210, 0.1)",
            "&:hover": {
              backgroundColor: "#63a4ff",
            },
          }}
        >
          Change Status
        </Button>
      ) : (
        <IconButton onClick={handleMenuClick} sx={{ position: "absolute", top: 8, right: 8 }}>
          <MoreVertIcon />
        </IconButton>
      )}

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEditModalOpen}>Edit Status</MenuItem>
      </Menu>

      <Modal open={isEditModalOpen} onClose={handleEditModalClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            backgroundColor: "white",
            padding: 4,
            borderRadius: 2,
            boxShadow: 24,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Edit Status
          </Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="status-label">Status</InputLabel>
            {isLoadingStatuses ? (
              <CircularProgress size={24} />
            ) : (
              <Select
                labelId="status-label"
                value={updatedStatus}
                onChange={(e) => setUpdatedStatus(e.target.value)}
                label="Status"
              >
                {statuses.map((status) => (
                  <MenuItem key={status.id} value={status.statusname}>
                    {status.statusname}
                  </MenuItem>
                ))}
              </Select>
            )}
          </FormControl>

          <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
            Add Comment
          </Typography>

          <TextField
            label="Comment"
            multiline
            fullWidth
            rows={3}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            sx={{ mb: 2 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={isInternal}
                onChange={(e) => setIsInternal(e.target.checked)}
              />
            }
            label="Internal Comment"
            sx={{ mb: 2 }}
          />

          <Button variant="outlined" component="label" fullWidth sx={{ mb: 2 }}>
            Attach File
            <input type="file" hidden ref={fileInputRef} />
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmitAll}
            fullWidth
            disabled={isSaving || isLoadingStatuses}
          >
            {isSaving ? "Submitting..." : "Submit All"}
          </Button>
        </Box>
      </Modal>

      {/* ✅ Snackbar for feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditWorkOrder;
